import { isMaterialCost } from "@/utils/sos/utilities";
import { CostTaxBreakdown } from "./queries/tracking-data/cost-tax-breakdown";
import { Item } from "./queries/tracking-data/items";

export type CostCategory = (typeof costCategories)[number];

const costCategories = ["material", "nonMaterial", "existingInventory", "labour", "shipping"] as const;

export class CategorizedCosts {
  private readonly costs: Record<CostCategory, CostTaxBreakdown>;

  public readonly material: CostTaxBreakdown = CostTaxBreakdown.ZERO;
  public readonly nonMaterial: CostTaxBreakdown = CostTaxBreakdown.ZERO;
  public readonly existingInventory: CostTaxBreakdown = CostTaxBreakdown.ZERO;
  public readonly labour: CostTaxBreakdown = CostTaxBreakdown.ZERO;
  public readonly shipping: CostTaxBreakdown = CostTaxBreakdown.ZERO;

  public static ZERO = new CategorizedCosts(
    Object.fromEntries(costCategories.map((category) => [category, CostTaxBreakdown.ZERO])),
  );

  constructor(costs: Partial<Record<CostCategory, CostTaxBreakdown>>) {
    this.costs = Object.fromEntries(
      costCategories.map((category) => [category, costs[category] ?? CostTaxBreakdown.ZERO]),
    ) as Record<CostCategory, CostTaxBreakdown>;

    for (const category of costCategories) {
      this[category] = this.costs[category];
    }
  }

  public add(other: CategorizedCosts): CategorizedCosts {
    return new CategorizedCosts(
      Object.fromEntries(costCategories.map((category) => [category, this.costs[category].add(other.costs[category])])),
    );
  }

  public multiply(multiplier: number): CategorizedCosts {
    return new CategorizedCosts(
      Object.fromEntries(costCategories.map((category) => [category, this.costs[category].multiply(multiplier)])),
    );
  }

  public getTotalCost(): CostTaxBreakdown {
    return Object.values(this.costs).reduce((total, cost) => total.add(cost), CostTaxBreakdown.ZERO);
  }

  public static getItemCostCategory(item: Item): CostCategory {
    if (item.id === 775) return "shipping";
    if (item.type === "Labor") return "labour";
    return isMaterialCost(item.type) ? "material" : "nonMaterial";
  }
}
